/**
 * Generates a full HTML document for the dashboard report (print/PDF).
 * All dashboard metrics and charts; design aligned with admin dashboard.
 */
import type {
  AdminAnalyticsData,
  AdminCohortRow,
  AdminFunnelStep,
  AdminTopClient,
  AdminSourcePerformance,
} from '../../api/admin.api';

const ACCENT = '#c2185b';
const MUTED_BAR_COLORS = ['#7c8fb5', '#8a9b7a', '#a67d6b', '#7d8fa8', '#9b8a7c', '#7a8e9e'];

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatRub(v: number): string {
  return `${Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ₽`;
}

function formatDateLabel(d: string): string {
  const parts = d.split('-');
  if (parts.length < 3) return d;
  const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  const m = months[parseInt(parts[1], 10) - 1] ?? parts[1];
  return `${parseInt(parts[2], 10)} ${m}`;
}

function buildChartSvg(
  data: { date: string; [k: string]: string | number }[],
  valueKey: string,
  max: number,
  formatValue: (v: number) => string,
  title: string,
  width: number = 520,
  height: number = 90
): string {
  if (!data.length) return '';
  const padding = { top: 8, right: 10, bottom: 24, left: 52 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const yScale = (v: number) =>
    max > 0 ? padding.top + innerH - (v / max) * innerH : padding.top + innerH / 2;
  const xStep = data.length > 1 ? innerW / (data.length - 1) : 0;
  const points = data.map((d, i) => ({
    x: padding.left + i * xStep,
    y: yScale(Number(d[valueKey]) || 0),
  }));
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const yTicks = max > 0 ? [0, max / 2, max] : [0];
  const gridColor = '#e8e8e8';

  let svg = `<svg width="${width}" height="${height}" style="display:block" role="img"><title>${escapeHtml(title)}</title>`;
  yTicks.forEach((tick) => {
    const y = yScale(tick);
    svg += `<line x1="${padding.left}" x2="${width - padding.right}" y1="${y}" y2="${y}" stroke="${gridColor}" stroke-width="1" stroke-dasharray="4 2"/>`;
    svg += `<text x="${padding.left - 6}" y="${y + 4}" text-anchor="end" font-size="9" fill="#666" font-family="system-ui,sans-serif">${escapeHtml(formatValue(tick))}</text>`;
  });
  svg += `<path d="${pathD}" fill="none" stroke="${ACCENT}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
  points.forEach((p) => {
    svg += `<circle cx="${p.x}" cy="${p.y}" r="2.5" fill="${ACCENT}"/>`;
  });
  const step = Math.max(1, Math.floor(data.length / 8));
  for (let i = 0; i < data.length; i++) {
    if (i % step !== 0 && i !== data.length - 1) continue;
    const x = padding.left + (data.length > 1 ? (i / (data.length - 1)) * innerW : 0);
    svg += `<text x="${x}" y="${height - 6}" text-anchor="middle" font-size="8" fill="#666" font-family="system-ui,sans-serif">${escapeHtml(formatDateLabel(data[i].date))}</text>`;
  }
  svg += '</svg>';
  return svg;
}

function sectionTitle(title: string): string {
  return `<h2 class="report-section-title">${escapeHtml(title)}</h2>`;
}

function metricCard(label: string, value: string | number, comparison?: number | null): string {
  const compSuffix =
    comparison != null && comparison !== 0
      ? ` <span class="report-metric-comp" style="font-size:11px;color:${comparison >= 0 ? '#0d7d3d' : '#c85050'}">(${comparison >= 0 ? '+' : ''}${comparison.toFixed(0)}%)</span>`
      : '';
  return `<div class="report-metric-card"><div class="report-metric-label">${escapeHtml(label)}</div><div class="report-metric-value">${typeof value === 'number' ? value : escapeHtml(String(value))}${compSuffix}</div></div>`;
}

function chartCard(title: string, svgContent: string): string {
  return `<div class="report-chart-card"><h2 class="report-section-title">${escapeHtml(title)}</h2><div class="report-chart-inner">${svgContent}</div></div>`;
}

function funnelHtml(funnel: AdminFunnelStep[]): string {
  if (!funnel.length) return '';
  const rows = funnel
    .map(
      (step, idx) => `
    <div class="report-funnel-row">
      <span class="report-funnel-label">${escapeHtml(step.label)}</span>
      <div class="report-funnel-bar-wrap">
        <div class="report-funnel-bar" style="width:${Math.min(100, step.percent)}%;background:${MUTED_BAR_COLORS[idx % MUTED_BAR_COLORS.length]};min-width:${step.percent > 0 ? '28px' : '0'}"></div>
      </div>
      <span class="report-funnel-count">${step.count} (${step.percent.toFixed(1)}%)</span>
    </div>`
    )
    .join('');
  return `<div class="report-section-card">${sectionTitle('Воронка')}<p class="report-subtitle">Доля от всех пользователей бота</p><div class="report-funnel">${rows}</div></div>`;
}

type SourceRow = { source: string; count: number; percent: number };

function sourcesHtml(sources: SourceRow[], trafficSources?: { source: string; count: number }[]): string {
  const list: SourceRow[] =
    sources.length > 0
      ? sources
      : (trafficSources ?? []).map((s) => {
          const total = (trafficSources ?? []).reduce((sum, x) => sum + x.count, 0);
          const pct = total > 0 ? (s.count / total) * 100 : 0;
          return { source: s.source, count: s.count, percent: pct };
        });
  if (!list.length) return '';
  const rows = list
    .map(
      (s, idx) => `
    <div class="report-source-row">
      <span class="report-source-label">${escapeHtml(s.source)}</span>
      <div class="report-source-bar-wrap">
        <div class="report-source-bar" style="width:${Math.min(100, s.percent)}%;background:${MUTED_BAR_COLORS[idx % MUTED_BAR_COLORS.length]};min-width:${s.percent > 0 ? '24px' : '0'}"></div>
      </div>
      <span class="report-source-count">${s.count} (${s.percent.toFixed(1)}%)</span>
    </div>`
    )
    .join('');
  return `<div class="report-section-card">${sectionTitle('Источники трафика')}<div class="report-sources">${rows}</div></div>`;
}

function cohortTableHtml(rows: AdminCohortRow[]): string {
  if (!rows.length) return '';
  const body = rows
    .map(
      (r) =>
        `<tr><td class="report-td">${escapeHtml(r.cohortMonth)}</td><td class="report-td report-num">${r.cohortSize}</td><td class="report-td report-num">${r.repeatCount}</td><td class="report-td report-num">${r.repeatPercent.toFixed(1)}%</td></tr>`
    )
    .join('');
  return `<div class="report-section-card">${sectionTitle('Когортный анализ')}<table class="report-table"><thead><tr><th class="report-th">Когорта</th><th class="report-th report-num">Размер</th><th class="report-th report-num">Повторные</th><th class="report-th report-num">%</th></tr></thead><tbody>${body}</tbody></table></div>`;
}

function top10Html(top: AdminTopClient[]): string {
  if (!top.length) return '';
  const body = top
    .slice(0, 10)
    .map(
      (c) =>
        `<tr><td class="report-td">${escapeHtml(c.telegramUsername || `id:${c.userId}`)}</td><td class="report-td report-num">${formatRub(c.totalSum)}</td></tr>`
    )
    .join('');
  return `<div class="report-section-card">${sectionTitle('Топ-10 клиентов по выручке')}<table class="report-table"><thead><tr><th class="report-th">Клиент</th><th class="report-th report-num">Сумма</th></tr></thead><tbody>${body}</tbody></table></div>`;
}

function sourcePerformanceTableHtml(rows: AdminSourcePerformance[]): string {
  if (!rows.length) return '';
  const body = rows
    .map(
      (r) =>
        `<tr><td class="report-td">${escapeHtml(r.source || '—')}</td><td class="report-td report-num">${r.ordersPeriod ?? 0}</td><td class="report-td report-num">${r.ordersTotal ?? 0}</td><td class="report-td report-num">${formatRub(r.revenuePeriod ?? 0)}</td><td class="report-td report-num">${formatRub(r.revenueTotal ?? 0)}</td></tr>`
    )
    .join('');
  return `<div class="report-section-card">${sectionTitle('Источники: заказы и выручка')}<p class="report-subtitle">По первому заказу клиента или start-параметру</p><table class="report-table"><thead><tr><th class="report-th">Источник</th><th class="report-th report-num">Заказов (период)</th><th class="report-th report-num">Заказов (всего)</th><th class="report-th report-num">Выручка (период)</th><th class="report-th report-num">Выручка (всего)</th></tr></thead><tbody>${body}</tbody></table></div>`;
}

export function getDashboardReportPrintHtml(
  data: AdminAnalyticsData,
  periodLabel: string
): string {
  const m = data.metrics;
  const charts = data.charts;
  const marketing = data.marketing ?? {};
  const funnel = marketing.funnel ?? [];
  const usersBySource = marketing.usersBySourcePercent ?? [];
  const trafficSources = charts?.trafficSources ?? data.topSources;
  const revenueByDay = charts?.revenueByDay ?? [];
  const usersGrowth = charts?.usersGrowth ?? [];
  const avgCheckByDay = charts?.avgCheckByDay ?? [];
  const cohort = marketing.cohortAnalysis ?? [];
  const top10 = data.top10Clients ?? [];

  const maxRevenue = Math.max(1, ...revenueByDay.map((d) => Number(d.total) || 0));
  const maxUsers = Math.max(1, ...usersGrowth.map((d) => Number(d.count) || 0));
  const maxAvgCheck = Math.max(1, ...avgCheckByDay.map((d) => Number(d.avgCheck) || 0));

  const periodText =
    data.periodFrom && data.periodTo
      ? `${periodLabel} (${data.periodFrom} — ${data.periodTo})`
      : periodLabel;

  const comparison = data.comparison;

  const revenueChartSvg = buildChartSvg(
    revenueByDay,
    'total',
    maxRevenue,
    (v) => formatRub(v),
    'Выручка по дням'
  );
  const usersChartSvg = buildChartSvg(
    usersGrowth,
    'count',
    maxUsers,
    (v) => String(Math.round(v)),
    'Рост пользователей'
  );
  const avgCheckChartSvg = buildChartSvg(
    avgCheckByDay,
    'avgCheck',
    maxAvgCheck,
    (v) => formatRub(v),
    'Средний чек по дням'
  );

  const sourcesForReport: SourceRow[] =
    usersBySource.length > 0
      ? usersBySource
      : (trafficSources ?? []).map((s) => {
          const total = (trafficSources ?? []).reduce((sum, x) => sum + x.count, 0);
          return { source: s.source, count: s.count, percent: total > 0 ? (s.count / total) * 100 : 0 };
        });

  const html = `<!DOCTYPE html>
<html lang="ru">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=210mm">
  <title>Отчёт дашборда</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 0; background: #f1f5f9; color: #1a1a1a; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 12px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    @page { size: A4; margin: 12mm; }
    .sheet { width: 210mm; padding: 14mm 16mm; margin: 0 auto; background: #fff; min-height: 100vh; }
    .report-top-bar { display: flex; justify-content: space-between; align-items: center; padding: 8px 0 16px 0; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; font-size: 12px; color: #64748b; }
    .report-shop-name { font-weight: 600; color: #334155; }
    .report-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
    .report-title { font-size: 26px; font-weight: 700; color: #0f172a; margin: 0; letter-spacing: -0.02em; }
    .report-period { font-size: 14px; color: #64748b; margin: 8px 0 0 0; font-weight: 500; }
    .report-section-title { font-size: 15px; font-weight: 600; color: #0f172a; margin: 0 0 12px 0; padding-bottom: 8px; border-bottom: 3px solid ${ACCENT}; }
    .report-subtitle { font-size: 11px; color: #64748b; margin: 0 0 10px 0; }
    .report-metrics-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px 18px; margin-bottom: 24px; }
    .report-metric-card { padding: 18px 20px; border: 2px solid #94a3b8; border-radius: 14px; background: #f8fafc; box-shadow: 0 4px 12px rgba(0,0,0,0.06); border-left: 6px solid ${ACCENT}; }
    .report-metric-label { font-size: 10px; color: #64748b; margin-bottom: 8px; letter-spacing: 0.05em; text-transform: uppercase; font-weight: 600; }
    .report-metric-value { font-size: 22px; font-weight: 700; color: #0f172a; font-variant-numeric: tabular-nums; }
    .report-chart-card { background: #fff; border: 2px solid #94a3b8; border-radius: 14px; padding: 20px 22px; margin-bottom: 22px; break-inside: avoid; box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
    .report-chart-inner { margin-top: 12px; }
    .report-section-card { background: #fff; border: 2px solid #94a3b8; border-radius: 14px; padding: 20px 22px; margin-bottom: 22px; break-inside: avoid; box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
    .report-funnel, .report-sources { display: flex; flex-direction: column; gap: 10px; }
    .report-funnel-row, .report-source-row { display: flex; align-items: center; gap: 12px; font-size: 12px; }
    .report-funnel-label, .report-source-label { min-width: 140px; color: #374151; font-weight: 500; }
    .report-funnel-bar-wrap, .report-source-bar-wrap { flex: 1; height: 24px; background: #f1f5f9; border-radius: 8px; overflow: hidden; min-width: 60px; }
    .report-funnel-bar, .report-source-bar { height: 100%; border-radius: 8px; }
    .report-funnel-count, .report-source-count { min-width: 80px; text-align: right; font-variant-numeric: tabular-nums; color: #64748b; font-size: 11px; font-weight: 500; }
    .report-user-metrics { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px 16px; }
    .report-user-metrics .report-metric-card { border-left-color: #64748b; }
    .report-table { width: 100%; border-collapse: collapse; font-size: 12px; border-radius: 10px; overflow: hidden; border: 1px solid #e2e8f0; }
    .report-th, .report-td { padding: 12px 14px; text-align: left; border-bottom: 1px solid #e2e8f0; }
    .report-th { font-weight: 600; color: #334155; background: #f8fafc; }
    .report-td { background: #fff; }
    .report-table tbody tr:nth-child(even) .report-td { background: #f8fafc; }
    .report-num { text-align: right; font-variant-numeric: tabular-nums; }
    .report-footer { margin-top: 28px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; }
    @media print {
      body { background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .sheet { box-shadow: none; }
      .report-metric-card, .report-chart-card, .report-section-card { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .report-funnel-bar, .report-source-bar { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="report-top-bar">
      <span class="report-shop-name">Бери Охапку</span>
      <span>${escapeHtml(new Date().toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }))}</span>
    </div>
    <div class="report-header">
      <div>
        <h1 class="report-title">Отчёт дашборда</h1>
        <p class="report-period">Период: ${escapeHtml(periodText)}</p>
      </div>
    </div>

    <div class="report-section-card">
      ${sectionTitle('Финансовые показатели')}
      <div class="report-metrics-grid">
        ${metricCard('Выручка', formatRub(Number(m?.totalRevenue) || 0), comparison?.revenuePercent)}
        ${metricCard('Себестоимость', formatRub(Number(m?.cogs) || 0))}
        ${metricCard('Валовая прибыль', formatRub(Number(m?.grossProfit) || 0), comparison?.grossProfitPercent)}
        ${metricCard('Маржинальность', `${Number(m?.grossMarginPercent ?? 0).toFixed(1)}%`)}
        ${metricCard('Оплаченных заказов', Number(m?.orderCount) || 0, comparison?.orderCountPercent)}
        ${metricCard('Средний чек (AOV)', formatRub(Number(m?.avgCheck) || 0))}
        ${metricCard('Выручка в день (средн.)', formatRub(Number(m?.revenuePerDayAvg) || 0))}
      </div>
    </div>

    <div class="report-section-card">
      ${sectionTitle('Пользователи')}
      <div class="report-metrics-grid">
        ${metricCard('Новые пользователи', Number(m?.newUsersPeriod) || 0, comparison?.newUsersPercent)}
        ${metricCard('Активные пользователи', Math.round(Number(m?.activeClients) ?? 0))}
        ${metricCard('Всего пользователей', Number(m?.userCount) ?? 0)}
        ${metricCard('ARPU', formatRub(Number(m?.arpu) || 0))}
      </div>
    </div>

    ${revenueByDay.length > 0 ? chartCard('Выручка по дням', revenueChartSvg) : ''}
    ${usersGrowth.length > 0 ? chartCard('Рост пользователей', usersChartSvg) : ''}
    ${avgCheckByDay.length > 0 ? chartCard('Средний чек по дням', avgCheckChartSvg) : ''}

    ${funnelHtml(funnel)}
    ${sourcesHtml(sourcesForReport, trafficSources)}
    ${sourcePerformanceTableHtml(marketing.sourcePerformance ?? [])}

    <div class="report-section-card">
      ${sectionTitle('Пользовательские метрики')}
      <p class="report-subtitle">Конверсии, retention, LTV и повторные покупки</p>
      <div class="report-user-metrics">
        ${metricCard('Повторные клиенты, %', `${Number(marketing.repeatPurchasesPercent ?? 0).toFixed(1)}%`)}
        ${metricCard('Retention 7 дн.', `${Number(m?.retention7Days ?? 0).toFixed(1)}%`)}
        ${metricCard('Retention 30 дн.', `${Number(m?.retention30Days ?? 0).toFixed(1)}%`)}
        ${metricCard('Конверсия в заказ', `${Number(m?.conversionToOrder ?? m?.conversion ?? 0).toFixed(1)}%`)}
        ${metricCard('Конверсия в оплату', `${Number(m?.conversionToPayment ?? 0).toFixed(1)}%`)}
        ${metricCard('Конверсия от входа до оплаты', `${Number(m?.conversionToPaymentFromStart ?? 0).toFixed(1)}%`)}
        ${metricCard('Пользователи без заказов, %', `${Number(marketing.clientsWithoutOrdersPercent ?? 0).toFixed(1)}%`)}
        ${metricCard('LTV', formatRub(Number(marketing.ltv ?? 0)))}
        ${metricCard('Среднее заказов на клиента', Number(marketing.avgOrdersPerClient ?? 0).toFixed(2))}
        ${metricCard('Средний интервал между заказами, дн.', data.avgOrderIntervalDays != null ? `${Number(data.avgOrderIntervalDays).toFixed(1)}` : '—')}
        ${marketing.cacPlaceholder != null && marketing.cacPlaceholder > 0 ? metricCard('CAC (расходы на привлечение)', formatRub(marketing.cacPlaceholder)) : ''}
      </div>
    </div>

    ${cohortTableHtml(cohort)}
    ${top10Html(top10)}

    <p class="report-footer">Сформировано ${escapeHtml(new Date().toLocaleString('ru-RU'))}. Сохраните как PDF в диалоге печати.</p>
  </div>
</body>
</html>`;

  return html;
}
